<script setup>
import { computed, ref, watch, defineModel } from 'vue';
import { useToggle } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import addMonths from 'date-fns/addMonths';
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';
import endOfDay from 'date-fns/endOfDay';
import endOfMonth from 'date-fns/endOfMonth';
import format from 'date-fns/format';
import startOfDay from 'date-fns/startOfDay';
import startOfMonth from 'date-fns/startOfMonth';
import subDays from 'date-fns/subDays';
import { vOnClickOutside } from '@vueuse/components';
import Button from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Modal from 'dashboard/components/Modal.vue';
import ModalHeader from 'dashboard/components/ModalHeader.vue';
import DatePicker from 'vue-datepicker-next';

const props = defineProps({
  allowAllTime: {
    type: Boolean,
    default: false,
  },
  defaultSelection: {
    type: String,
    default: '',
  },
  prefixLabel: {
    type: String,
    default: '',
  },
  icon: {
    type: String,
    default: '',
  },
});

const emit = defineEmits([
  'rangeTypeChange',
  'monthOffsetChange',
  'customRangeChange',
]);

const fromModel = defineModel('from', { type: Date, default: null });
const toModel = defineModel('to', { type: Date, default: null });
const daysNumModel = defineModel('daysNum', { type: Number, default: null });

const { t, locale } = useI18n();

const DATE_FILTER_TYPES = {
  ALL: 'all',
  DAY: 'day',
  MONTH: 'month',
  CUSTOM: 'custom',
};

const DATE_FILTER_ACTION = 'select_date_range';
const CUSTOM_DATE_ACTION = 'open_custom_modal';

const showCustomModal = ref(false);
const customDateRange = ref([new Date(), new Date()]);
const customFormattedLabel = ref('');

const allTimeItem = computed(() => {
  if (!props.allowAllTime) return [];
  return [
    {
      label: t('OVERVIEW_REPORTS.CONTACT_STAGES.ALL_TIME'),
      value: 'all_time',
      action: DATE_FILTER_ACTION,
      type: DATE_FILTER_TYPES.ALL,
    },
  ];
});

const dayMenuItemConfigs = computed(() => [
  {
    label: t('REPORT.DATE_RANGE_OPTIONS.LAST_7_DAYS'),
    value: 'last_7_days',
    action: DATE_FILTER_ACTION,
    type: DATE_FILTER_TYPES.DAY,
    daysBefore: 6,
  },
  {
    label: t('REPORT.DATE_RANGE_OPTIONS.LAST_14_DAYS'),
    value: 'last_14_days',
    action: DATE_FILTER_ACTION,
    type: DATE_FILTER_TYPES.DAY,
    daysBefore: 13,
  },
  {
    label: t('REPORT.DATE_RANGE_OPTIONS.LAST_30_DAYS'),
    value: 'last_30_days',
    action: DATE_FILTER_ACTION,
    type: DATE_FILTER_TYPES.DAY,
    daysBefore: 29,
  },
]);

const resolvedLocale = computed(() => {
  const currentLocale =
    locale.value ||
    (typeof navigator !== 'undefined' ? navigator.language : 'en');
  return currentLocale.replace('_', '-');
});

const monthFormatter = computed(
  () =>
    new Intl.DateTimeFormat(resolvedLocale.value, {
      month: 'long',
      year: 'numeric',
    })
);

const monthMenuItemConfigs = computed(() => {
  const now = new Date();
  const offsets = [0, -1, -2, -3];

  return offsets.map(offset => ({
    label:
      offset === 0
        ? t('REPORT.DATE_RANGE_OPTIONS.THIS_MONTH')
        : monthFormatter.value.format(addMonths(now, offset)),
    value: offset === 0 ? 'this_month' : `month_${offset}`,
    action: DATE_FILTER_ACTION,
    type: DATE_FILTER_TYPES.MONTH,
    monthOffset: offset,
  }));
});

const customMenuItem = computed(() => [
  {
    label: t('REPORT.DATE_RANGE_OPTIONS.CUSTOM_DATE_RANGE'),
    value: 'custom',
    action: CUSTOM_DATE_ACTION,
    type: DATE_FILTER_TYPES.CUSTOM,
  },
]);

const selectedDateRangeValue = ref('');
const [showDropdown, toggleDropdown] = useToggle();
const monthOffset = ref(0);

const menuItems = computed(() => {
  const selectedValue = selectedDateRangeValue.value;
  return [
    ...allTimeItem.value,
    ...dayMenuItemConfigs.value,
    ...monthMenuItemConfigs.value,
    ...customMenuItem.value,
  ].map(config => ({
    ...config,
    isSelected: selectedValue === config.value,
  }));
});

const defaultInitialValue = computed(() => {
  if (props.defaultSelection) return props.defaultSelection;
  if (props.allowAllTime) return 'all_time';
  return 'last_7_days';
});

selectedDateRangeValue.value = defaultInitialValue.value;

const menuSections = computed(() => {
  const allItems = menuItems.value.filter(
    item => item.type === DATE_FILTER_TYPES.ALL
  );
  const dayItems = menuItems.value.filter(
    item => item.type === DATE_FILTER_TYPES.DAY
  );
  const monthItems = menuItems.value.filter(
    item => item.type === DATE_FILTER_TYPES.MONTH
  );
  const customItems = menuItems.value.filter(
    item => item.type === DATE_FILTER_TYPES.CUSTOM
  );

  return [
    { items: allItems },
    { items: dayItems },
    { items: monthItems },
    { items: customItems },
  ].filter(section => section.items.length > 0);
});

const selectedConfig = computed(
  () =>
    menuItems.value.find(
      menuItem => menuItem.value === selectedDateRangeValue.value
    ) || menuItems.value[0]
);

const rawSelectedLabel = computed(() => {
  if (selectedDateRangeValue.value === 'custom' && customFormattedLabel.value) {
    return customFormattedLabel.value;
  }
  const selectedItem = menuItems.value.find(
    item => item.value === selectedDateRangeValue.value
  );
  return selectedItem?.label || '';
});

const computeRange = config => {
  if (!config) {
    return null;
  }

  if (config.type === DATE_FILTER_TYPES.ALL) {
    return { from: null, to: null, daysBefore: null };
  }

  if (config.type === DATE_FILTER_TYPES.CUSTOM) {
    if (!customDateRange.value || customDateRange.value.length < 2) return null;
    const from = startOfDay(customDateRange.value[0]);
    const to = endOfDay(customDateRange.value[1]);
    const daysBefore = differenceInCalendarDays(to, from);
    return { from, to, daysBefore };
  }

  if (config.type === DATE_FILTER_TYPES.MONTH) {
    const now = new Date();
    const baseMonthStart = startOfMonth(addMonths(now, monthOffset.value));
    const from = startOfDay(baseMonthStart);
    const isCurrentMonth =
      config.value === 'this_month' && monthOffset.value === 0;
    const to = isCurrentMonth
      ? endOfDay(now)
      : endOfDay(endOfMonth(baseMonthStart));
    const daysBefore = differenceInCalendarDays(to, from);
    return { from, to, daysBefore };
  }

  const to = endOfDay(new Date());
  const from = startOfDay(subDays(to, Number(config.daysBefore)));
  return { from, to, daysBefore: Number(config.daysBefore) };
};

const applySelection = config => {
  if (!config) return;

  if (config.type === DATE_FILTER_TYPES.MONTH) {
    monthOffset.value = config.monthOffset || 0;
  } else {
    monthOffset.value = 0;
  }

  const range = computeRange(config);
  if (!range) return;

  const { from, to, daysBefore } = range;
  fromModel.value = from;
  toModel.value = to;
  daysNumModel.value = daysBefore;

  emit('rangeTypeChange', config.type);
  emit('monthOffsetChange', monthOffset.value);
};

const handleAction = ({ action, value }) => {
  toggleDropdown(false);
  if (action === CUSTOM_DATE_ACTION) {
    showCustomModal.value = true;
    return;
  }
  if (action !== DATE_FILTER_ACTION) {
    return;
  }
  selectedDateRangeValue.value = value;
};

const applyCustomDates = () => {
  if (
    customDateRange.value &&
    customDateRange.value.length === 2 &&
    customDateRange.value[0] &&
    customDateRange.value[1]
  ) {
    const from = startOfDay(customDateRange.value[0]);
    const to = endOfDay(customDateRange.value[1]);
    customFormattedLabel.value = `${format(from, 'dd MMM yyyy')} - ${format(to, 'dd MMM yyyy')}`;
    selectedDateRangeValue.value = 'custom';
    fromModel.value = from;
    toModel.value = to;
    daysNumModel.value = differenceInCalendarDays(to, from);
    emit('rangeTypeChange', DATE_FILTER_TYPES.CUSTOM);
    emit('customRangeChange', { from, to });
    showCustomModal.value = false;
  }
};

watch(
  () => selectedConfig.value,
  config => {
    if (config?.value !== 'custom') {
      applySelection(config);
    }
  },
  { immediate: true }
);
</script>

<template>
  <div
    v-on-click-outside="() => toggleDropdown(false)"
    class="relative flex items-center group"
  >
    <button
      type="button"
      class="inline-flex items-center h-8 gap-1.5 px-2.5 text-xs font-medium transition-colors border rounded-lg cursor-pointer border-n-container bg-n-solid-1 hover:bg-n-alpha-1 text-n-slate-12 focus:outline-hidden"
      @click="toggleDropdown()"
    >
      <span
        v-if="icon"
        class="size-3.5 flex-shrink-0 opacity-70 text-n-slate-10"
        :class="[icon]"
      />
      <span v-if="prefixLabel" class="font-normal text-n-slate-10">
        {{ prefixLabel }}:
      </span>
      <span
        class="font-semibold text-n-slate-12 truncate max-w-[140px] sm:max-w-[220px]"
      >
        {{ rawSelectedLabel }}
      </span>
      <span class="i-lucide-chevron-down size-3 text-n-slate-10 ml-0.5" />
    </button>

    <DropdownMenu
      v-if="showDropdown"
      :menu-items="menuItems"
      :menu-sections="menuSections"
      class="mt-1 ltr:right-0 rtl:left-0 xl:ltr:right-0 xl:rtl:left-0 top-full z-50 min-w-[180px]"
      @action="handleAction($event)"
    />

    <!-- Modal para Rango de Fechas Personalizado -->
    <Modal
      v-if="showCustomModal"
      :show="showCustomModal"
      :on-close="() => (showCustomModal = false)"
    >
      <ModalHeader
        :header-title="t('REPORT.DATE_RANGE_OPTIONS.CUSTOM_DATE_RANGE')"
        :header-content="t('REPORT.CUSTOM_DATE_RANGE.PLACEHOLDER')"
      />
      <div class="p-6 flex flex-col items-center gap-6">
        <div class="date-picker-wrapper w-full flex justify-center">
          <DatePicker
            v-model:value="customDateRange"
            range
            inline
            :clearable="false"
            :editable="false"
          />
        </div>
        <div class="flex items-center justify-end gap-3 w-full">
          <Button
            sm
            slate
            faded
            :label="t('OVERVIEW_REPORTS.CONTACT_STAGES.CANCEL')"
            @click="showCustomModal = false"
          />
          <Button
            sm
            blue
            :label="t('REPORT.CUSTOM_DATE_RANGE.CONFIRM') || 'Aplicar'"
            @click="applyCustomDates"
          />
        </div>
      </div>
    </Modal>
  </div>
</template>
